import { Box, Divider, Stack, Tooltip } from '@mui/material';
import { QuestionIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Alert } from 'common';

import { AssistantInstructionsFormFields } from './AssistantInstructions/FormFields';
import { AssistantToolsFormFields } from './AssistantTools/FormFields';
import { AssistantVoiceFormFields } from './AssistantVoice/FormFields';
import { CallerPhoneNumberFormField } from './CallerPhoneNumberFormField';
import { PreAssistantMessageFormFields } from './PreAssistantMessage/FormFields';
import { CallTransferFormFields } from './VoiceCallTransfer/FormFields';
import { FeatureFlagKey } from '../../../../../../../utils/flags/schemas';
import { useFlag } from '../../../../../../../utils/flags/useFlag';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { CommunicationWindowFormFields } from '../shared/CommunicationWindow';

/**
 * Keep this in sync with the editable fields in DefinitionForm staff/src/scenes/VoiceAssistants/DefinitionForm.tsx
 */

// Required fields
const CALLEE_PHONE_NUMBER_HELPER_TEXT = `Phone number to be called. US and Canadian numbers are supported.`;

enum Sections {
  Instructions = 'Instructions',
  Voice = 'Voice',
  Tools = 'Tools',
  Transfer = 'Transfer',
  CommunicationWindow = 'CommunicationWindow',
  PreAssistantMessage = 'PreAssistantMessage',
}

export function VoiceCallFormFields() {
  const [expanded, setExpanded] = useState<Sections | null>(Sections.CommunicationWindow);
  const canConfigurePreAssistantMessage = useFlag({
    flagKey: FeatureFlagKey.canConfigurePreAssistantMessage,
  });

  return (
    <Stack gap={2}>
      <Stack spacing={2}>
        <BlockValueTemplateField
          fieldName="config.calleePhoneNumber"
          label="Callee Phone Number"
          labelAdornment={
            <Tooltip placement="top" arrow title={CALLEE_PHONE_NUMBER_HELPER_TEXT}>
              <Box>
                <QuestionIcon />
              </Box>
            </Tooltip>
          }
          placeholder="+1-###-###-#### or curly braces for dynamic number"
          required
        />
        <CallerPhoneNumberFormField />
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack>
        <AssistantInstructionsFormFields
          expanded={expanded === Sections.Instructions}
          onChange={(_event, isExpanded) => setExpanded(isExpanded ? Sections.Instructions : null)}
        />
        <AssistantVoiceFormFields
          expanded={expanded === Sections.Voice}
          onChange={(_event, isExpanded) => setExpanded(isExpanded ? Sections.Voice : null)}
        />
        <AssistantToolsFormFields
          expanded={expanded === Sections.Tools}
          onChange={(_event, isExpanded) => setExpanded(isExpanded ? Sections.Tools : null)}
        />
        <CallTransferFormFields
          expanded={expanded === Sections.Transfer}
          onChange={(_event, isExpanded) => setExpanded(isExpanded ? Sections.Transfer : null)}
        />
        {canConfigurePreAssistantMessage && (
          <PreAssistantMessageFormFields
            expanded={expanded === Sections.PreAssistantMessage}
            onChange={(_event, isExpanded) =>
              setExpanded(isExpanded ? Sections.PreAssistantMessage : null)
            }
          />
        )}
        <CommunicationWindowFormFields
          expanded={expanded === Sections.CommunicationWindow}
          setIsExpanded={(isExpanded) =>
            setExpanded(isExpanded ? Sections.CommunicationWindow : null)
          }
          description="Set the valid windows during which communications can be sent. If a voice call is initiated outside of this window, the block will error out."
          unrestrictedWarning={
            <Alert variant="warning" title="Calls can be placed at any time.">
              With Valid Windows turned off, this block may place calls during nights, weekends, or
              other unintended hours.
            </Alert>
          }
        />
      </Stack>
    </Stack>
  );
}
